import React, { useState, useEffect, useContext } from 'react';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ROLE_PERMISSIONS } from '../config/permissions';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  Group,
  Settings,
  Security,
  Description,
} from '@mui/icons-material';

// Import UserManagement from the projects folder where you moved it
import UserManagement from '../components/projects/UserManagement';

// Helper to get month names
const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Helper to get years (e.g., last 5 years)
const getYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = 0; i < 5; i++) {
    years.push(currentYear - i);
  }
  return years;
};

export default function AdminPanel() {
  const { user, role } = useContext(AuthContext);
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [adminError, setAdminError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [adminDocs, setAdminDocs] = useState([]);
  const [loadingAdminDocs, setLoadingAdminDocs] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const availableYears = getYears();
  const [adminSubTab, setAdminSubTab] = useState(0);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [updatingBatch, setUpdatingBatch] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('AdminPanel - Current user:', user);
    console.log('AdminPanel - Current role:', role);
  }, [user, role]);

  // Update admin check
  useEffect(() => {
    if (user === null) return;

    if (!user || role !== 'admin') {
      console.log('Access denied - User:', user, 'Role:', role);
      navigate('/');
    }
  }, [user, role, navigate]);

  // Fetch admin document data based on selected month/year
  const fetchDocumentAdminData = async (month, year) => {
    setLoadingAdminDocs(true);
    setAdminError(null);

    try {
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 1);
      endDate.setMilliseconds(endDate.getMilliseconds() - 1);

      const { data, error } = await supabase
        .from('document_admin')
        .select('id, name, status, gefactureerd, created_at')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      setAdminDocs(data || []);

    } catch (error) {
      console.error('Error fetching admin document data:', error);
      setAdminError('Fout bij ophalen administratie gegevens: ' + error.message);
      setAdminDocs([]);
    } finally {
      setLoadingAdminDocs(false);
      setSelectedDocIds([]);
    }
  };

  // Toggle 'gefactureerd' status
  const handleToggleGefactureerd = async (docId, currentStatus) => {
    const newValue = !currentStatus;
    setAdminDocs(prevDocs =>
      prevDocs.map(doc =>
        doc.id === docId ? { ...doc, gefactureerd: newValue } : doc
      )
    );

    try {
      const { error } = await supabase
        .from('document_admin')
        .update({ gefactureerd: newValue })
        .eq('id', docId);

      if (error) {
        setAdminDocs(prevDocs =>
          prevDocs.map(doc =>
            doc.id === docId ? { ...doc, gefactureerd: currentStatus } : doc
          )
        );
        throw error;
      }
    } catch (error) {
      console.error('Error updating gefactureerd status:', error);
      setAdminError('Kon status niet bijwerken: ' + error.message);
    }
  };

  // Handle selection changes
  const handleSelectOne = (event, id) => {
    const selectedIndex = selectedDocIds.indexOf(id);
    let newSelectedDocIds = [];

    if (selectedIndex === -1) {
      newSelectedDocIds = newSelectedDocIds.concat(selectedDocIds, id);
    } else if (selectedIndex === 0) {
      newSelectedDocIds = newSelectedDocIds.concat(selectedDocIds.slice(1));
    } else if (selectedIndex === selectedDocIds.length - 1) {
      newSelectedDocIds = newSelectedDocIds.concat(selectedDocIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelectedDocIds = newSelectedDocIds.concat(
        selectedDocIds.slice(0, selectedIndex),
        selectedDocIds.slice(selectedIndex + 1),
      );
    }
    setSelectedDocIds(newSelectedDocIds);
  };

  const handleSelectAll = (event, docsToSelect) => {
    if (event.target.checked) {
      const newSelecteds = docsToSelect.map((n) => n.id);
      setSelectedDocIds(newSelecteds);
      return;
    }
    setSelectedDocIds([]);
  };

  // Handle batch update to gefactureerd
  const handleBatchUpdateGefactureerd = async () => {
    if (selectedDocIds.length === 0) {
      setAdminError("Selecteer eerst documenten om te markeren.");
      return;
    }
    setUpdatingBatch(true);
    setAdminError(null);

    try {
      const { error } = await supabase
        .from('document_admin')
        .update({ gefactureerd: true })
        .in('id', selectedDocIds);

      if (error) throw error;

      const updatedDocs = adminDocs.map(doc =>
        selectedDocIds.includes(doc.id)
          ? { ...doc, gefactureerd: true }
          : doc
      );
      setAdminDocs(updatedDocs);
      setSelectedDocIds([]);

    } catch (error) {
      console.error('Error batch updating gefactureerd status:', error);
      setAdminError('Batch update mislukt: ' + error.message);
    } finally {
      setUpdatingBatch(false);
    }
  };

  // Fetch data when tab becomes active or month/year changes
  useEffect(() => {
    if (activeTab === 3 && role === 'admin') {
      fetchDocumentAdminData(selectedMonth, selectedYear);
    }
    if (activeTab !== 3) {
      setAdminSubTab(0);
    }
  }, [activeTab, selectedMonth, selectedYear, role]);

  // Filter documents based on the selected sub-tab
  const getFilteredAdminDocs = () => {
    switch (adminSubTab) {
      case 0:
        return adminDocs.filter(doc => doc.status === 'intake');
      case 1:
        return adminDocs.filter(doc => ['to_review', 'reviewed', 'exported'].includes(doc.status));
      case 2:
        return adminDocs.filter(doc => ['failed', 'faulted'].includes(doc.status));
      case 3:
        return adminDocs.filter(doc => doc.gefactureerd);
      default:
        return adminDocs;
    }
  };

  const filteredDocs = getFilteredAdminDocs();

  // Handle CSV Export
  const handleExportCSV = () => {
    if (filteredDocs.length === 0) {
      setAdminError("Geen documenten om te exporteren.");
      return;
    }

    // Define headers
    const headers = ["Naam", "Status", "Gefactureerd", "Aangemaakt op"];
    // Format data rows
    const rows = filteredDocs.map(doc => [
      `"${doc.name?.replace(/"/g, '""') || 'N/A'}"`, // Escape double quotes
      `"${doc.status || 'N/A'}"`, // Ensure strings are quoted
      doc.gefactureerd ? '"Ja"' : '"Nee"' ,
      `"${doc.created_at ? new Date(doc.created_at).toLocaleString() : 'N/A'}"`
    ].join(',')); // Join cells with commas

    // Combine headers and rows
    const csvContent = [headers.join(','), ...rows].join('\n');

    // Create Blob and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    // Construct filename based on filters
    const subTabName = ['Geupload', 'Verwerkt', 'Gefaald', 'Gefactureerd'][adminSubTab];
    const monthString = (selectedMonth + 1).toString().padStart(2, '0');
    const filename = `documenten_${selectedYear}-${monthString}_${subTabName}.csv`;
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up
  };

  return (
    <Container maxWidth="lg" sx={{
      mt: '80px',
      mb: 4,
      pt: 2,
      minHeight: 'calc(100vh - 80px)',
    }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper elevation={3} sx={{ p: 3, width: '100%' }}>
        <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Admin Panel Tabs"
        >
          <Tab label="Gebruikers" icon={<Group />} iconPosition="start" />
          <Tab label="Rollen & Permissies" icon={<Security />} iconPosition="start" />
          <Tab label="Systeem" icon={<Settings />} iconPosition="start" />
          <Tab label="Administratie" icon={<Description />} iconPosition="start" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <UserManagement />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Rol Permissies Overzicht
            </Typography>
            {Object.entries(ROLE_PERMISSIONS).map(([role, permissions]) => (
              <Card key={`role-${role}`} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ textTransform: 'capitalize', mb: 1 }}>
                    {role}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {permissions.map((permission) => (
                      <Chip
                        key={`${role}-${permission}`}
                        label={permission}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Systeem Instellingen
            </Typography>
            <Card>
              <CardContent>
                <Typography>
                  Systeem instellingen komen binnenkort beschikbaar.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Document Administratie
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3, alignItems: 'flex-end' }}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="month-select-label">Maand</InputLabel>
                  <Select
                    labelId="month-select-label"
                    value={selectedMonth}
                    label="Maand"
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    {monthNames.map((name, index) => (
                      <MenuItem key={name} value={index}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="year-select-label">Jaar</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={selectedYear}
                    label="Jaar"
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleExportCSV}
                  disabled={filteredDocs.length === 0 || loadingAdminDocs}
                  fullWidth
                >
                  Exporteer naar CSV
                </Button>
              </Grid>
              {[0, 1, 2].includes(adminSubTab) && (
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleBatchUpdateGefactureerd}
                    disabled={selectedDocIds.length === 0 || loadingAdminDocs || updatingBatch}
                    fullWidth
                  >
                    {updatingBatch ? 'Bezig...' : `Markeer ${selectedDocIds.length} als Gefactureerd`}
                  </Button>
                </Grid>
              )}
            </Grid>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={adminSubTab}
                onChange={(e, newValue) => setAdminSubTab(newValue)}
                aria-label="Document Status Filter Tabs"
              >
                <Tab label="Geupload" />
                <Tab label="Verwerkt" />
                <Tab label="Gefaald" />
                <Tab label="Gefactureerd" />
              </Tabs>
            </Box>

            {adminError && <Alert severity="error" sx={{ mb: 2 }}>{adminError}</Alert>}

            {loadingAdminDocs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={1}>
                <Table sx={{ minWidth: 650 }} aria-label="document administration table">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                      <TableCell padding="checkbox">
                        {[0, 1, 2].includes(adminSubTab) && filteredDocs.length > 0 && (
                          <Checkbox
                            color="primary"
                            indeterminate={
                              selectedDocIds.length > 0 &&
                              selectedDocIds.length < filteredDocs.length
                            }
                            checked={
                              filteredDocs.length > 0 &&
                              selectedDocIds.length === filteredDocs.length
                            }
                            onChange={(e) => handleSelectAll(e, filteredDocs)}
                            inputProps={{
                              'aria-label': 'select all documents on this page',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>Naam</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Gefactureerd</TableCell>
                      <TableCell>Aangemaakt op</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDocs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          {adminDocs.length === 0
                            ? "Geen documenten gevonden voor deze periode."
                            : "Geen documenten met deze status gevonden."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDocs.map((doc) => {
                        const isItemSelected = selectedDocIds.indexOf(doc.id) !== -1;
                        const labelId = `checkbox-list-label-${doc.id}`;

                        return (
                          <TableRow
                            hover
                            onClick={(event) => {[0, 1, 2].includes(adminSubTab) && handleSelectOne(event, doc.id)}}
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={doc.id}
                            selected={isItemSelected}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: [0, 1, 2].includes(adminSubTab) ? 'pointer' : 'default' }}
                          >
                            <TableCell padding="checkbox">
                              {[0, 1, 2].includes(adminSubTab) && (
                                <Checkbox
                                  color="primary"
                                  checked={isItemSelected}
                                  inputProps={{
                                    'aria-labelledby': labelId,
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell component="th" id={labelId} scope="row">
                              {doc.name || 'N/A'}
                            </TableCell>
                            <TableCell>{doc.status || 'N/A'}</TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={!!doc.gefactureerd}
                                onChange={() => handleToggleGefactureerd(doc.id, !!doc.gefactureerd)}
                                disabled={adminSubTab === 3}
                                inputProps={{ 'aria-label': `Markeer ${doc.name || 'document'} als gefactureerd` }}
                              />
                            </TableCell>
                            <TableCell>
                              {doc.created_at ? new Date(doc.created_at).toLocaleString() : 'N/A'}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}


